import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Users, DollarSign, Calendar, Activity, Bed, MessageSquare, Settings,
  LogOut, PieChart as PieChartIcon, BarChart3, Clock, ChevronLeft, ChevronRight,
  ArrowLeft, Building2, MapPin, Phone, Mail, Globe, Star, Info,
} from 'lucide-react';
import './AdminHotelApprovals.css';

const statusPillVariant = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'active':      return 'positive';
    case 'inactive':    return 'pending';
    case 'maintenance': return 'negative';
    default:            return 'pending';
  }
};

function HotelDetails() {
  const { id } = useParams();
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        if (!currentUser) throw new Error('Not authenticated');
        const idToken = await currentUser.getIdToken();
        const res = await fetch(`/api/admin/hotels/${id}`, {
          headers: { Authorization: `Bearer ${idToken}` },
          credentials: 'include',
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || 'Failed to load hotel');
        if (!cancelled) setHotel(data);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load hotel.');
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [id, currentUser]);

  const navItems = [
    { path: '/admin',               Icon: PieChartIcon,  label: 'Analytics' },
    { path: '/admin/approvals',     Icon: Clock,         label: 'Hotel approvals' },
    { path: '/admin/hotel',         Icon: Bed,           label: 'Hotel management' },
    { path: '/admin/users',         Icon: Users,         label: 'User management' },
    { path: '/admin/notifications', Icon: MessageSquare, label: 'Notifications' },
    { path: '/iot-management',      Icon: Activity,      label: 'IoT management' },
    { path: '/admin/bookings',      Icon: Calendar,      label: 'Bookings' },
    { path: '/admin/revenue',       Icon: DollarSign,    label: 'Revenue' },
    { path: '/admin/reports',       Icon: BarChart3,     label: 'Reports' },
    { path: '/admin/settings',      Icon: Settings,      label: 'Settings' },
  ];

  const isActive = (path) => path === '/admin/hotel'
    ? location.pathname.startsWith('/admin/hotel')
    : location.pathname === path;

  const renderShell = (children) => (
    <div className="admin-dashboard admin-page">
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="brand">
            <span className="brand-mark" aria-hidden="true">IB</span>
            {sidebarOpen && (
              <span className="brand-wordmark">
                InnBotics
                <span className="brand-eyebrow">Admin Console</span>
              </span>
            )}
          </div>
          <button
            className="toggle-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {sidebarOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
          </button>
        </div>
        <nav className="sidebar-nav" aria-label="Admin navigation">
          {navItems.map(({ path, Icon, label }) => (
            <button
              key={path}
              onClick={() => navigate(path)}
              className={`nav-item ${isActive(path) ? 'active' : ''}`}
              title={!sidebarOpen ? label : undefined}
            >
              <span className="nav-icon"><Icon size={18} strokeWidth={1.75} /></span>
              {sidebarOpen && <span>{label}</span>}
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <button onClick={async () => { await logout(); navigate('/login'); }} className="logout-btn">
            <span className="nav-icon"><LogOut size={18} strokeWidth={1.75} /></span>
            {sidebarOpen && <span>Sign out</span>}
          </button>
        </div>
      </aside>
      <main className={`main-content ${sidebarOpen ? '' : 'expanded'}`}>{children}</main>
    </div>
  );

  if (loading) {
    return renderShell(
      <div className="ib-card ib-card--padded">
        <div className="approval-loading">
          <div className="approval-spinner" aria-hidden="true" />
          <p>Loading hotel details…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return renderShell(
      <>
        <button className="ib-btn ib-btn--ghost ib-btn--sm" style={{ marginBottom: 18 }} onClick={() => navigate('/admin/hotel')}>
          <ArrowLeft size={14} /> Back to hotels
        </button>
        <div className="approval-error">{error}</div>
      </>
    );
  }

  if (!hotel) {
    return renderShell(
      <div className="ib-card ib-card--padded">
        <div className="ib-empty">
          <h3>Hotel not found</h3>
          <p>This property may have been removed or the link is incorrect.</p>
        </div>
      </div>
    );
  }

  const Field = ({ label, value }) => (
    <div>
      <dt>{label}</dt>
      <dd>{value !== undefined && value !== null && value !== '' ? value : '—'}</dd>
    </div>
  );

  const amenitiesText = Array.isArray(hotel.amenities)
    ? hotel.amenities.join(', ')
    : (typeof hotel.amenities === 'string' ? hotel.amenities : '');

  return renderShell(
    <>
      <button className="ib-btn ib-btn--ghost ib-btn--sm" style={{ marginBottom: 18 }} onClick={() => navigate('/admin/hotel')}>
        <ArrowLeft size={14} /> Back to hotels
      </button>

      <header className="ib-page-header" style={{ marginBottom: 22 }}>
        <div>
          <p className="ib-eyebrow">Hotel detail</p>
          <h1 className="ib-display">{hotel.name || 'Unnamed property'}</h1>
          <p className="ib-lede">
            {[hotel.city, hotel.state, hotel.country].filter(Boolean).join(', ') || 'Location unspecified'}
          </p>
        </div>
        {hotel.status && (
          <span className={`ib-pill ib-pill--${statusPillVariant(hotel.status)}`}>
            {hotel.status}
          </span>
        )}
      </header>

      <section className="details-section">
        <h3><Building2 size={16} strokeWidth={1.7} style={{ verticalAlign: '-2px', marginRight: 6 }} />Property</h3>
        <dl className="details-grid">
          <Field label="Hotel name" value={hotel.name} />
          <Field label="Hotel ID" value={hotel.id} />
          <Field label="Status" value={hotel.status} />
          <Field label="Rating" value={hotel.rating != null ? `${hotel.rating} ★` : null} />
          <Field label="Total rooms" value={hotel.total_rooms} />
          <Field label="Active bookings" value={hotel.active_bookings} />
          <Field label="Total staff" value={hotel.total_staff} />
          <Field label="Created at" value={hotel.created_at && new Date(hotel.created_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })} />
        </dl>
        {hotel.description && (
          <div style={{ marginTop: 14 }}>
            <dt style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ib-text-3)', marginBottom: 6 }}>Description</dt>
            <dd style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: 'var(--ib-text-1)' }}>{hotel.description}</dd>
          </div>
        )}
      </section>

      <section className="details-section">
        <h3><MapPin size={16} strokeWidth={1.7} style={{ verticalAlign: '-2px', marginRight: 6 }} />Location</h3>
        <dl className="details-grid">
          <Field label="Address" value={hotel.address} />
          <Field label="City" value={hotel.city} />
          <Field label="State" value={hotel.state} />
          <Field label="Country" value={hotel.country} />
          <Field label="Postal code" value={hotel.postal_code} />
        </dl>
      </section>

      <section className="details-section">
        <h3><Phone size={16} strokeWidth={1.7} style={{ verticalAlign: '-2px', marginRight: 6 }} />Contact</h3>
        <dl className="details-grid">
          <Field label="Phone" value={hotel.phone} />
          <Field label="Email" value={hotel.email} />
          <Field label="Website" value={hotel.website} />
        </dl>
      </section>

      {amenitiesText && (
        <section className="details-section">
          <h3><Star size={16} strokeWidth={1.7} style={{ verticalAlign: '-2px', marginRight: 6 }} />Amenities</h3>
          <dl className="details-grid">
            <Field label="Listed amenities" value={amenitiesText} />
          </dl>
        </section>
      )}
    </>
  );
}

export default HotelDetails;
